<script setup lang="ts">
import { computed } from 'vue'
import { RouterLink, RouterView, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@/stores/auth'
import { useThemeStore } from '@/stores/theme'
const { t } = useI18n()
const auth = useAuthStore()
const theme = useThemeStore()
const route = useRoute()

const isDiscover = () => route.path === '/' || route.path === '/discover'
const isFullBleed = () => Boolean(route.meta.fullBleed)
const isSessions = () => route.path === '/sessions'
const isSpeakers = () => route.path === '/speakers'
const isProfile = () => route.path === '/profile' || route.path.startsWith('/profile/')

/** FAB only for signed-in users on non-discover main pages */
const showCreateSessionFab = computed(
  () => !isDiscover() && auth.isAuthenticated && auth.canCreateSession,
)
const showSpeakerRequestFab = computed(
  () => !isDiscover() && auth.isAuthenticated && !auth.canCreateSession,
)
</script>

<template>
  <div class="min-h-screen min-h-dvh flex flex-col bg-background-light dark:bg-background-dark text-text-primary-light dark:text-text-primary-dark pb-safe">
    <!-- Optional top bar: minimal header for main app (can be extended per-route for back + title) -->
    <header class="sticky top-0 z-20 bg-surface-light dark:bg-surface-dark border-b border-gray-200 dark:border-gray-800 pt-[env(safe-area-inset-top)]">
      <div class="w-full max-w-md lg:max-w-6xl mx-auto px-4 h-14 flex items-center justify-between gap-3">
        <RouterLink to="/" class="flex items-center gap-2 font-display font-bold text-lg text-primary min-w-0 shrink">
          <span class="text-xl shrink-0">🕌</span>
          <span class="truncate">{{ t('app.name') }}</span>
        </RouterLink>
        <div class="flex items-center gap-1.5 shrink-0">
          <button
            type="button"
            class="p-2 rounded-xl text-text-secondary-light dark:text-text-secondary-dark hover:text-primary hover:bg-primary/10 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
            :aria-label="theme.isDark ? 'Light mode' : 'Dark mode'"
            @click="theme.toggle()"
          >
            <span class="material-symbols-outlined text-2xl">{{ theme.isDark ? 'light_mode' : 'dark_mode' }}</span>
          </button>
          <RouterLink
            v-if="!auth.isAuthenticated"
            to="/login"
            class="text-sm font-medium text-text-secondary-light dark:text-text-secondary-dark hover:text-primary"
          >
            {{ t('nav.login') }}
          </RouterLink>
          <RouterLink
            v-if="!auth.isAuthenticated"
            to="/register"
            class="px-4 py-2 rounded-xl bg-primary text-white text-sm font-semibold hover:opacity-90"
          >
            {{ t('nav.register') }}
          </RouterLink>
          <RouterLink
            v-else
            to="/profile"
            class="text-sm font-medium text-text-secondary-light dark:text-text-secondary-dark hover:text-primary"
          >
            {{ t('nav.profile') }}
          </RouterLink>
        </div>
      </div>
    </header>

    <!-- Main content: full-bleed for map pages, constrained for others -->
    <main
      class="flex-1 w-full overflow-x-hidden"
      :class="isFullBleed()
        ? 'max-w-none px-0 pb-0 lg:max-w-6xl lg:mx-auto lg:px-4 lg:pb-28'
        : 'max-w-md lg:max-w-6xl mx-auto px-4 pb-28'"
    >
      <RouterView />
    </main>

    <!-- FAB: authenticated only; SVG icons so the glyph never depends on font load -->
    <RouterLink
      v-if="showCreateSessionFab"
      to="/sessions/new"
      class="fixed bottom-24 end-6 lg:end-[max(1.5rem,calc((100vw-min(100vw,72rem))/2+1.5rem))] z-30 w-14 h-14 rounded-full bg-primary text-white shadow-lg shadow-primary/40 flex items-center justify-center hover:scale-105 active:scale-95 transition-transform border-4 border-surface-light dark:border-surface-dark"
      aria-label="Add session"
    >
      <svg class="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true">
        <path stroke-linecap="round" d="M12 5v14M5 12h14" />
      </svg>
    </RouterLink>
    <RouterLink
      v-else-if="showSpeakerRequestFab"
      to="/speakers/request"
      class="fixed bottom-24 end-6 lg:end-[max(1.5rem,calc((100vw-min(100vw,72rem))/2+1.5rem))] z-30 w-14 h-14 rounded-full bg-amber-500 text-white shadow-lg shadow-amber-500/40 flex items-center justify-center hover:scale-105 active:scale-95 transition-transform border-4 border-surface-light dark:border-surface-dark"
      :aria-label="t('speaker_request.become_speaker')"
    >
      <svg class="w-8 h-8" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M12 14a3 3 0 0 0 3-3V5a3 3 0 1 0-6 0v6a3 3 0 0 0 3 3Zm5-3a1 1 0 1 1 2 0 7 7 0 0 1-6 6.92V20h2a1 1 0 1 1 0 2h-6a1 1 0 1 1 0-2h2v-2.08A7 7 0 0 1 5 11a1 1 0 1 1 2 0 5 5 0 1 0 10 0Z" />
      </svg>
    </RouterLink>

    <!-- Bottom tab bar: Discover, Sessions, Speakers, Profile -->
    <nav class="fixed bottom-0 inset-x-0 z-50 bg-surface-light dark:bg-surface-dark border-t border-gray-200 dark:border-gray-800 py-2 pb-safe shadow-[0_-4px_20px_-10px_rgba(0,0,0,0.1)] dark:shadow-[0_-4px_20px_-10px_rgba(0,0,0,0.3)]">
      <div class="w-full max-w-md lg:max-w-lg mx-auto flex justify-evenly items-center px-2">
      <RouterLink
        to="/"
        class="flex flex-col items-center justify-center flex-1 max-w-[80px] min-h-[44px] py-2 rounded-xl transition-colors"
        :class="isDiscover() ? 'text-primary bg-primary/10' : 'text-text-secondary-light dark:text-text-secondary-dark hover:text-primary'"
      >
        <span class="material-symbols-outlined text-2xl" :class="isDiscover() ? 'fill-1' : ''">explore</span>
        <span class="text-[10px] font-medium mt-0.5">{{ t('nav.discover') }}</span>
      </RouterLink>
      <RouterLink
        to="/sessions"
        class="flex flex-col items-center justify-center flex-1 max-w-[80px] min-h-[44px] py-2 rounded-xl transition-colors"
        :class="isSessions() ? 'text-primary bg-primary/10' : 'text-text-secondary-light dark:text-text-secondary-dark hover:text-primary'"
      >
        <span class="material-symbols-outlined text-2xl" :class="isSessions() ? 'fill-1' : ''">event</span>
        <span class="text-[10px] font-medium mt-0.5">{{ t('nav.sessions') }}</span>
      </RouterLink>
      <RouterLink
        to="/speakers"
        class="flex flex-col items-center justify-center flex-1 max-w-[80px] min-h-[44px] py-2 rounded-xl transition-colors"
        :class="isSpeakers() ? 'text-primary bg-primary/10' : 'text-text-secondary-light dark:text-text-secondary-dark hover:text-primary'"
      >
        <span class="material-symbols-outlined text-2xl" :class="isSpeakers() ? 'fill-1' : ''">mic</span>
        <span class="text-[10px] font-medium mt-0.5">{{ t('nav.speakers') }}</span>
      </RouterLink>
      <RouterLink
        :to="auth.isAuthenticated ? '/profile' : '/profile/prayer-notifications'"
        class="flex flex-col items-center justify-center flex-1 max-w-[80px] min-h-[44px] py-2 rounded-xl transition-colors"
        :class="isProfile() ? 'text-primary bg-primary/10' : 'text-text-secondary-light dark:text-text-secondary-dark hover:text-primary'"
      >
        <span class="material-symbols-outlined text-2xl" :class="isProfile() ? 'fill-1' : ''">person</span>
        <span class="text-[10px] font-medium mt-0.5">{{ t('nav.profile') }}</span>
      </RouterLink>
      </div>
    </nav>
  </div>
</template>
